/**
 * Workout runner (SPEC §7.2): flattens steps into a timeline, updates the ERG target every second
 * (ramps interpolate), supports skip, extend +1 min, global intensity offset (±1 %), watt nudges (±5 W),
 * auto-laps at interval boundaries. Ramp test ends when cadence < 50 rpm for 5 s.
 */
import type { WorkoutDirective, WorkoutHook } from '@/ride/session';
import { findSegment, flatten, segmentWatts, totalDuration, type Segment } from './flatten';
import type { Workout } from './model';

export const FTP20_LABEL = 'FTP-Test 20 min';
export const RAMP_STOP_RPM = 50;
export const RAMP_STOP_SEC = 5;

export interface RunnerView {
  elapsedSec: number;
  totalSec: number;
  index: number;
  segment: Segment | null;
  next: Segment | null;
  segmentRemainingSec: number;
  targetW: number | null;
  nextTargetW: number | null;
  intensityPct: number;
  offsetW: number;
  done: boolean;
  doneReason: 'completed' | 'ramp-cadence' | 'manual' | null;
}

export class WorkoutRunner implements WorkoutHook {
  readonly workout: Workout;
  segments: Segment[];
  private elapsed = 0;
  private idx = 0;
  intensityPct = 0;
  offsetW = 0;
  private finished = false;
  private doneReason: RunnerView['doneReason'] = null;
  private lowCadenceFor = 0;
  private pendingLap: string | undefined;
  private lastTargetSecond = -1;
  private cachedTarget: number | null = null;

  constructor(
    workout: Workout,
    private readonly getFtp: () => number,
    resumeElapsedSec = 0,
  ) {
    this.workout = workout;
    this.segments = flatten(workout.steps);
    if (resumeElapsedSec > 0) {
      this.elapsed = resumeElapsedSec;
      this.idx = findSegment(this.segments, this.elapsed);
      if (this.idx >= this.segments.length) this.finished = true;
    }
  }

  get elapsedSec(): number {
    return this.elapsed;
  }

  get done(): boolean {
    return this.finished;
  }

  get currentSegment(): Segment | null {
    return this.segments[this.idx] ?? null;
  }

  private label(seg: Segment | undefined): string {
    if (!seg) return '';
    const base = seg.label ?? (seg.kind === 'free' ? 'Frei' : seg.kind === 'ramp' ? 'Rampe' : 'Intervall');
    return seg.rep ? `${base} ${seg.rep.n}/${seg.rep.of}` : base;
  }

  /** Target watts at an absolute elapsed time, after intensity offset and watt nudges. */
  targetAt(t: number, segIdx = findSegment(this.segments, t)): number | null {
    const seg = this.segments[segIdx];
    if (!seg) return null;
    const base = segmentWatts(seg, t, this.getFtp());
    if (base === null) return null;
    return Math.max(0, Math.round(base * (1 + this.intensityPct / 100) + this.offsetW));
  }

  tick(dtSec: number, ctx: { cadenceRpm: number; powerW: number; paused: boolean }): WorkoutDirective {
    if (this.finished) return { targetW: null, free: true, forceVs: false, done: true };
    if (!ctx.paused) this.elapsed += dtSec;
    // Ramp test termination (after the warm-up).
    if (this.workout.special === 'ramp-test' && this.idx > 0) {
      if (ctx.cadenceRpm < RAMP_STOP_RPM) this.lowCadenceFor += dtSec;
      else this.lowCadenceFor = 0;
      if (this.lowCadenceFor >= RAMP_STOP_SEC) {
        this.finish('ramp-cadence');
        return { targetW: null, free: true, forceVs: false, done: true, lapLabel: 'Ende' };
      }
    }
    let lapLabel: string | undefined = this.pendingLap;
    this.pendingLap = undefined;
    let i = findSegment(this.segments, this.elapsed);
    if (i !== this.idx) {
      this.idx = i;
      lapLabel = this.label(this.segments[i]);
      this.lastTargetSecond = -1;
    }
    if (i >= this.segments.length) {
      if (lapLabel !== undefined) lapLabel = 'Nach dem Workout';
      this.finish('completed');
      return {
        targetW: null,
        free: true,
        forceVs: false,
        done: true,
        ...(lapLabel !== undefined ? { lapLabel } : {}),
      };
    }
    i = this.idx;
    const seg = this.segments[i]!;
    // ERG target updated once per second (SPEC §7.2)
    const sec = Math.floor(this.elapsed);
    if (sec !== this.lastTargetSecond) {
      this.lastTargetSecond = sec;
      this.cachedTarget = this.targetAt(sec + 0.5, i);
    }
    const isFtp20Block = this.workout.special === 'ftp20' && seg.label === FTP20_LABEL;
    const d: WorkoutDirective = {
      targetW: seg.kind === 'free' ? null : this.cachedTarget,
      free: seg.kind === 'free',
      forceVs: isFtp20Block,
      done: false,
    };
    if (isFtp20Block) d.flatGrade = true;
    if (lapLabel !== undefined) d.lapLabel = lapLabel;
    return d;
  }

  skip(): void {
    if (this.finished) return;
    const seg = this.segments[this.idx];
    if (!seg) return;
    this.elapsed = seg.start + seg.durationSec;
  }

  /** Extend the current interval by 60 s (shifts everything after it). */
  extend(sec = 60): void {
    const seg = this.segments[this.idx];
    if (!seg) return;
    seg.durationSec += sec;
    for (let j = this.idx + 1; j < this.segments.length; j++) this.segments[j]!.start += sec;
    this.lastTargetSecond = -1;
  }

  adjustIntensity(deltaPct: number): void {
    this.intensityPct = Math.max(-50, Math.min(50, this.intensityPct + deltaPct));
    this.lastTargetSecond = -1;
  }

  adjustWatts(deltaW: number): void {
    this.offsetW += deltaW;
    this.lastTargetSecond = -1;
  }

  stop(): void {
    this.finish('manual');
  }

  private finish(reason: NonNullable<RunnerView['doneReason']>): void {
    if (this.finished) return;
    this.finished = true;
    this.doneReason = reason;
  }

  view(): RunnerView {
    const total = totalDuration(this.segments);
    const seg = this.segments[this.idx] ?? null;
    const next = this.segments[this.idx + 1] ?? null;
    return {
      elapsedSec: this.elapsed,
      totalSec: total,
      index: this.idx,
      segment: seg,
      next,
      segmentRemainingSec: seg ? Math.max(0, seg.start + seg.durationSec - this.elapsed) : 0,
      targetW: seg && seg.kind !== 'free' ? this.targetAt(this.elapsed, this.idx) : null,
      nextTargetW: next && next.kind !== 'free' ? this.targetAt(next.start + 0.01, this.idx + 1) : null,
      intensityPct: this.intensityPct,
      offsetW: this.offsetW,
      done: this.finished,
      doneReason: this.doneReason,
    };
  }
}
